import React from 'react';

import Grid from '@material-ui/core/Grid';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {Route, useRouteMatch} from 'react-router-dom';
import {toast} from 'react-toastify';

import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {Icon} from 'components/ui-kit/Icon';
import {
    ContentColumns,
    CopyButton,
    Id,
    StatusWrapper,
    Title,
    TitleText,
    TitleWrapper,
} from 'components/ui-kit/PageComponents';
import {RouterSwitch, RouterTab, RouterTabList} from 'components/ui-kit/RouterTabs';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {colors} from 'theme';
import {MovingReport} from 'types/documents';
import {useClipboard} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';

import {Products} from './Products';
import {DataTitle, Error, ErrorsWrapper} from './styled';

interface MovingDetailPageProps {
    data: MovingReport;
}

export function convertData(data: MovingReport, t: TFunction): MovingReport {
    return {
        reportId: handleNil(data?.reportId),
        createdAt: handleNil(data?.createdAt, (value: string) =>
            formatDate(value, 'dd.MM.yyyy, HH:mm'),
        ),
        reportType: data?.reportType,
        status: handleNil(data?.status, (value: string) =>
            t(`dictionaries.documents.status.${value}`),
        ),
        errors: data.errors,
        reportInfo: {
            employeeFullName: handleNil(data?.reportInfo?.employeeFullName),
            storageLocationFrom: handleNil(data?.reportInfo?.storageLocationFrom),
            storageLocationTo: handleNil(data?.reportInfo?.storageLocationTo),
            note: handleNil(data?.reportInfo?.note),
        },
    };
}

export const Moving = ({data}: MovingDetailPageProps) => {
    const {t} = useTranslation();
    const {path, url} = useRouteMatch();
    const copy = useClipboard();

    const convertedData = React.useMemo(() => convertData(data, t), [data, t]);

    const copyCode = () => {
        copy(convertedData.reportId);
        toast.success(t('pages.documents.details.idCopied'));
    };

    return (
        <>
            <TitleWrapper>
                <TitleText>
                    <Title>
                        {t('pages.documents.details.Moving.id')} <Id>{convertedData.reportId}</Id>
                    </Title>
                    <CopyButton
                        icon={
                            <Icon
                                source={CopyIcon}
                                width={15}
                                height={15}
                                fillColor={colors.brandPrimary}
                            />
                        }
                        onClick={copyCode}
                        hoverIndication={true}
                        label={t('pages.documents.details.copyId')}
                        labelPlace={'bottom'}
                    />
                </TitleText>
                <StatusWrapper color={DOCUMENT_STATUS_COLORS[data.status ?? 'DEFAULT']}>
                    {convertedData.status}
                </StatusWrapper>
            </TitleWrapper>

            <RouterTabList baseUrl={url}>
                <RouterTab url="">{t('pages.documents.detail.commonInfo')}</RouterTab>
                <RouterTab url="products">{t('pages.documents.detail.products')}</RouterTab>
            </RouterTabList>

            <RouterSwitch rootPath={path}>
                <Route path={`${path}`} exact>
                    <ErrorsWrapper>
                        {convertedData.errors?.map((err, index) => (
                            <Error key={index}>{err.message ?? ''}</Error>
                        ))}
                    </ErrorsWrapper>
                    <Grid container>
                        <Grid item xs={12} md={4}>
                            <DataTitle>{t('pages.documents.details.Moving.generalInfo')}</DataTitle>
                        </Grid>
                    </Grid>
                    <ContentColumns
                        data={[
                            {
                                createdAt: convertedData.createdAt,
                                employeeFullName: convertedData.reportInfo.employeeFullName,
                            },
                        ]}
                        tNameSpace="pages.documents.details.Moving"
                    />
                    <ContentColumns
                        data={[
                            {
                                storageLocationFrom: convertedData.reportInfo.storageLocationFrom,
                                storageLocationTo: convertedData.reportInfo.storageLocationTo,
                                ...(convertedData.reportInfo.note && {
                                    note: convertedData.reportInfo.note,
                                }),
                            },
                        ]}
                        tNameSpace="pages.documents.details.Moving"
                    />
                </Route>
                <Route path={`${path}/products`} exact>
                    <Products reportId={convertedData.reportId} />
                </Route>
            </RouterSwitch>
        </>
    );
};
